import { useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CodeIcon from '@mui/icons-material/Code';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import { MarkdownView } from './MarkdownView';

/**
 * Reusable Markdown editor.
 *
 * Uses MUI's `FormControl` + `InputLabel` so the floating label placement
 * matches `TextField` exactly — the label sits above the notched outline
 * and uses the same typography, colors, and required asterisk.
 *
 * The border is drawn via a `fieldset` + `legend` pair, matching MUI's
 * `OutlinedInput` structure, so the label notches the border when the
 * field is focused.
 *
 * Parent owns the value. The stored text is raw Markdown.
 */

type Mode = 'write' | 'preview';

export interface MarkdownEditorProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  required?: boolean;
  helperText?: string;
}

export function MarkdownEditor({
  label,
  value,
  onChange,
  placeholder,
  rows = 12,
  required,
  helperText,
}: MarkdownEditorProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('write');
  const [focused, setFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const wrapSelection = useCallback(
    (before: string, after: string = before, placeholderText: string = '') => {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const selected = value.slice(start, end) || placeholderText;
      const next = value.slice(0, start) + before + selected + after + value.slice(end);
      onChange(next);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + before.length, start + before.length + selected.length);
      }, 0);
    },
    [value, onChange]
  );

  const prefixLines = useCallback(
    (prefix: string) => {
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = (() => {
        const nextNewline = value.indexOf('\n', end);
        return nextNewline === -1 ? value.length : nextNewline;
      })();
      const block = value.slice(lineStart, lineEnd);
      const lines = block.length === 0 ? [''] : block.split('\n');
      const updated = lines
        .map((line, idx) => {
          const actualPrefix = prefix === '1. ' ? `${idx + 1}. ` : prefix;
          return `${actualPrefix}${line}`;
        })
        .join('\n');
      const next = value.slice(0, lineStart) + updated + value.slice(lineEnd);
      onChange(next);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(lineStart, lineStart + updated.length);
      }, 0);
    },
    [value, onChange]
  );

  const insertLink = useCallback(() => {
    // eslint-disable-next-line no-alert
    const url = window.prompt(t('markdown.linkPrompt'));
    if (!url) return;
    wrapSelection('[', `](${url})`, t('markdown.linkText'));
  }, [wrapSelection, t]);

  // The label always stays shrunk — our content has a toolbar at the top,
  // so there's no room for a non-shrunk "ghost" label inside the textarea.
  return (
    <FormControl fullWidth required={required} focused={focused}>
      {label && (
        <InputLabel shrink variant="outlined">
          {label}
        </InputLabel>
      )}

      <Box
        sx={{
          position: 'relative',
          borderRadius: 1,
          // Matches MUI OutlinedInput's `mt: 16px` so MarkdownEditor aligns
          // vertically with sibling TextFields when they share a Stack.
          mt: label ? '16px' : 0,
        }}
      >
        {/* Notched fieldset border — mirrors MUI's NotchedOutline structure.
            `top: -5px` + `padding: 0 8px` + legend with an invisible inner
            span is the exact recipe `OutlinedInput` uses so the label "sits"
            on the border line and the legend creates the horizontal cutout. */}
        <Box
          component="fieldset"
          aria-hidden="true"
          sx={{
            textAlign: 'left',
            position: 'absolute',
            bottom: 0,
            right: 0,
            top: '-5px',
            left: 0,
            margin: 0,
            padding: '0 8px',
            pointerEvents: 'none',
            borderRadius: 'inherit',
            borderStyle: 'solid',
            borderWidth: focused ? 2 : 1,
            borderColor: focused ? 'primary.main' : 'rgba(255, 255, 255, 0.23)',
            overflow: 'hidden',
            minWidth: '0%',
            '& > legend': {
              float: 'unset',
              width: label ? 'auto' : '0',
              overflow: 'hidden',
              display: 'block',
              padding: 0,
              height: '11px',
              fontSize: '0.75em',
              visibility: 'hidden',
              maxWidth: label ? '100%' : '0.01px',
              whiteSpace: 'nowrap',
              '& > span': {
                paddingLeft: '5px',
                paddingRight: '5px',
                display: 'inline-block',
                opacity: 0,
                visibility: 'visible',
              },
            },
          }}
        >
          {label && (
            <legend>
              {/* Matches the label width exactly (including required asterisk)
                  so the notch is the right size. InputLabel renders the
                  visible label itself. */}
              <span>{required ? `${label} *` : label}</span>
            </legend>
          )}
        </Box>

        {/* Content: toolbar + textarea/preview */}
        <Box sx={{ position: 'relative', borderRadius: 'inherit', overflow: 'hidden' }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              px: 0.5,
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexWrap: 'wrap',
            }}
          >
            <ToggleButtonGroup
              value={mode}
              exclusive
              size="small"
              onChange={(_, next: Mode | null) => next && setMode(next)}
              sx={{ mr: 1 }}
            >
              <ToggleButton value="write" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                {t('markdown.write')}
              </ToggleButton>
              <ToggleButton value="preview" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                {t('markdown.preview')}
              </ToggleButton>
            </ToggleButtonGroup>

            {mode === 'write' && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip title={t('markdown.bold')}>
                  <IconButton
                    size="small"
                    onClick={() => wrapSelection('**', '**', t('markdown.bold'))}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('markdown.italic')}>
                  <IconButton
                    size="small"
                    onClick={() => wrapSelection('_', '_', t('markdown.italic'))}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('markdown.link')}>
                  <IconButton size="small" onClick={insertLink}>
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('markdown.code')}>
                  <IconButton size="small" onClick={() => wrapSelection('`', '`', 'code')}>
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip title={t('markdown.quote')}>
                  <IconButton size="small" onClick={() => prefixLines('> ')}>
                    <FormatQuoteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('markdown.bulletList')}>
                  <IconButton size="small" onClick={() => prefixLines('- ')}>
                    <FormatListBulletedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('markdown.numberedList')}>
                  <IconButton size="small" onClick={() => prefixLines('1. ')}>
                    <FormatListNumberedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>

          {mode === 'write' ? (
            <Box
              component="textarea"
              ref={textareaRef}
              value={value}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={placeholder}
              required={required}
              rows={rows}
              sx={{
                width: '100%',
                border: 'none',
                outline: 'none',
                resize: 'vertical',
                p: 1.5,
                bgcolor: 'transparent',
                color: 'text.primary',
                fontFamily: 'inherit',
                fontSize: '1rem',
                lineHeight: 1.5,
                display: 'block',
                '&::placeholder': {
                  color: 'text.disabled',
                },
              }}
            />
          ) : (
            <Box
              sx={{
                p: 2,
                minHeight: rows * 24,
              }}
            >
              {value.trim() ? (
                <MarkdownView>{value}</MarkdownView>
              ) : (
                <Typography variant="body2" color="text.disabled">
                  {t('markdown.emptyPreview')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <FormHelperText>{helperText ?? t('markdown.hint')}</FormHelperText>
    </FormControl>
  );
}
